import { assetsService } from '@services/assets.service';
import { useStore } from '@store/store';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

interface DownloadPdfOptions {
  /** Whether to open the PDF in a new tab instead of downloading */
  openInNewTab?: boolean;
  /** Custom filename for download (without extension) */
  filename?: string;
  /** Show success toast after download */
  showSuccessToast?: boolean;
  /** Custom auth token for the request */
  customAuthToken?: string;
}

interface DownloadPdfResult {
  /** Function to trigger the PDF download */
  downloadPdf: (assetId: string, options?: DownloadPdfOptions) => Promise<string | null>;
  /** Whether the download is in progress */
  isDownloading: boolean;
  /** Error message if download failed */
  error: string | null;
  /** Clear error state */
  clearError: () => void;
}

/**
 * Hook to download PDF assets from a case record
 *
 * @param caseRecordId - The case record ID containing the asset
 * @returns Object with downloadPdf function and state
 *
 * @example
 * ```typescript
 * const { downloadPdf, isDownloading } = useDownloadPdf(caseId);
 *
 * // Download and trigger file download
 * await downloadPdf(assetId, { filename: 'fax-document' });
 *
 * // Open in new tab
 * await downloadPdf(assetId, { openInNewTab: true });
 *
 * // Get blob URL for custom usage
 * const blobUrl = await downloadPdf(assetId);
 * // Use blobUrl with <iframe>, <embed>, or react-pdf <Document file={blobUrl} />
 * ```
 */
export const useDownloadPdf = (caseRecordId: string | null): DownloadPdfResult => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const validateContext = useCallback(
    (assetId: string) => {
      if (!caseRecordId) {
        return 'Case record ID is required';
      }
      if (!selectedOrganizationId || !selectedProgramId) {
        return 'Organization and program context is required';
      }
      if (!assetId) {
        return 'Asset ID is required';
      }
      return null;
    },
    [caseRecordId, selectedOrganizationId, selectedProgramId],
  );

  const handleBlobAction = useCallback(
    (
      blobUrl: string,
      options: { openInNewTab?: boolean; filename?: string; showSuccessToast?: boolean },
    ) => {
      const { openInNewTab, filename, showSuccessToast } = options;

      if (openInNewTab) {
        window.open(blobUrl, '_blank');
        if (showSuccessToast) {
          toast.success('PDF opened in new tab');
        }
      } else if (filename) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        if (showSuccessToast) {
          toast.success('PDF downloaded successfully');
        }

        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      }
    },
    [],
  );

  const downloadPdf = useCallback(
    async (assetId: string, options?: DownloadPdfOptions): Promise<string | null> => {
      const { openInNewTab = false, filename, showSuccessToast = false } = options || {};

      const validationError = validateContext(assetId);
      if (validationError) {
        setError(validationError);
        toast.error(validationError);
        return null;
      }

      setIsDownloading(true);
      setError(null);

      try {
        const pdfBlob = await assetsService.downloadAssetFromArcus(
          {
            organizationId: selectedOrganizationId!,
            programId: selectedProgramId!,
            caseRecordId: caseRecordId!,
          },
          assetId,
          'pdf',
        );

        const blobUrl = URL.createObjectURL(pdfBlob);
        handleBlobAction(blobUrl, { openInNewTab, filename, showSuccessToast });

        return blobUrl;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to download PDF';
        setError(errorMsg);
        toast.error(errorMsg);
        console.error('PDF download error:', err);
        return null;
      } finally {
        setIsDownloading(false);
      }
    },
    [validateContext, handleBlobAction, selectedOrganizationId, selectedProgramId, caseRecordId],
  );

  return {
    downloadPdf,
    isDownloading,
    error,
    clearError,
  };
};
